import { AnimatedContentLoader, Input, InputProps } from '@components'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContactType } from '@root/constants'
import {
  DropdownWrapper,
  Header,
  HeaderIcon,
  Item,
  ItemColumn,
  ScrollableContent,
  StyledBusinessDropdown
} from './BusinessSearchDropdown.styled'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDebouncedValue } from '@root/hooks'
import { IYTJCompanySearchResult } from '@query/integrations/ytj'
import { IHandelsregisterSearchResult } from '@query/integrations/handelsregister'
import { useHandelsregisterSearch, useYTJSearch } from '@root/query/hooks'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { autoUpdate, flip, offset, size, useDismiss, useFloating } from '@floating-ui/react'
import { FaSearch } from 'react-icons/fa'
import styled from 'styled-components'

export type CompanyRegistrySource = 'ytj' | 'handelsregister'

export type CompanyRegistryHit =
  | {
      source: 'ytj'
      key: string
      name: string
      secondary: string
      company: IYTJCompanySearchResult
    }
  | {
      source: 'handelsregister'
      key: string
      name: string
      secondary: string
      company: IHandelsregisterSearchResult
    }

type Props = {
  label: string
  source: CompanyRegistrySource
  businessSlug?: string
  onSelect: (hit: CompanyRegistryHit) => void
} & Omit<InputProps, 'label' | 'onSelect'>

const toYtjHits = (data: IYTJCompanySearchResult[] | undefined): CompanyRegistryHit[] =>
  (data ?? [])
    .filter(company => company?.name != null)
    .map(company => ({
      source: 'ytj' as const,
      key: company.business_id,
      name: company.name ?? '',
      secondary: company.business_id,
      company
    }))

const toHandelsregisterHits = (
  data: IHandelsregisterSearchResult[] | undefined
): CompanyRegistryHit[] =>
  (data ?? [])
    .filter(company => company?.name != null)
    .map(company => ({
      source: 'handelsregister' as const,
      key: company.handelsregisternummer,
      name: company.name,
      secondary: company.register_number,
      company
    }))

const BusinessSearchDropdown: React.FC<Props> = ({ label, source, businessSlug, onSelect }) => {
  const [isFetchEnabled, setIsFetchEnabled] = useState(false)
  const [hasUserTyped, setHasUserTyped] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const { t } = useTranslation()

  const {
    register,
    control,
    formState: { errors }
  } = useFormContext()

  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))

  const [selectedType, nameForSearch] = useWatch({
    control,
    name: ['type', 'name']
  })

  const { debouncedValue: debouncedSearchTerm, setValue: setDebouncedSearchTerm } =
    useDebouncedValue<string>({
      initialValue: nameForSearch,
      delay: 200
    })
  useEffect(() => {
    setDebouncedSearchTerm(nameForSearch)
  }, [nameForSearch])

  const searchTerm =
    selectedType === ContactType.BUSINESS && isFetchEnabled ? debouncedSearchTerm : undefined

  const isSearchLongEnough = (searchTerm?.length ?? 0) >= 3

  const ytjSearch = useYTJSearch(searchTerm, source === 'ytj')
  const hrSearch = useHandelsregisterSearch(searchTerm, source === 'handelsregister', businessSlug)
  const { query, errorMessage } = source === 'handelsregister' ? hrSearch : ytjSearch
  const { isLoading, isError } = query
  const searchResults =
    source === 'handelsregister'
      ? toHandelsregisterHits(hrSearch.query.data)
      : toYtjHits(ytjSearch.query.data)

  const choose = (hit: CompanyRegistryHit) => {
    setIsFetchEnabled(false)
    setHasUserTyped(false)
    onSelect(hit)
  }

  const showDropdown = hasUserTyped && isFetchEnabled && selectedType === ContactType.BUSINESS

  const handleClose = () => {
    setIsFetchEnabled(false)
    setHasUserTyped(false)
    setFocusIndex(-1)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const len = searchResults.length
    if (!len && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      e.preventDefault()
      setFocusIndex(-1)
      return
    }

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        const newIndex = focusIndex === len - 1 ? 0 : Math.min(focusIndex + 1, len - 1)
        setFocusIndex(newIndex)
        itemRefs.current.get(newIndex)?.scrollIntoView({ block: 'nearest' })
        break
      }

      case 'ArrowUp': {
        e.preventDefault()
        const newIndex = focusIndex === 0 ? len - 1 : Math.max(focusIndex - 1, 0)
        setFocusIndex(newIndex)
        itemRefs.current.get(newIndex)?.scrollIntoView({ block: 'nearest' })
        break
      }

      case 'Enter': {
        const hit = searchResults[focusIndex]
        if (hit) choose(hit)
        handleClose()
        e.stopPropagation()
        e.preventDefault()
        break
      }

      case 'Escape': {
        handleClose()
        break
      }
    }
  }

  const MAX_DROPDOWN_HEIGHT = 300

  const { context, x, y, refs, strategy } = useFloating({
    open: showDropdown,
    onOpenChange: open => {
      if (!open) handleClose()
    },
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [
      offset(0),
      flip(),
      size({
        apply: ({ availableWidth, availableHeight, elements }) => {
          Object.assign(elements.floating.style, {
            width: `${Math.min(500, availableWidth - 48)}px`,
            maxHeight: `${Math.min(MAX_DROPDOWN_HEIGHT, availableHeight - 48)}px`
          })
        }
      })
    ]
  })

  useDismiss(context)

  const name = register('name', { required: { value: true, message: t('validation.required') } })

  return (
    <DropdownWrapper ref={refs.setReference}>
      <Input
        autoComplete="off"
        label={
          <LabelRow>
            <FaSearch />
            {label}
          </LabelRow>
        }
        onKeyDown={onKeyDown}
        required={true}
        errors={errors?.name}
        {...name}
        onChange={e => {
          setIsFetchEnabled(true)
          setHasUserTyped(true)
          name.onChange(e)
        }}
        onBlur={e => {
          name.onBlur(e)
          handleClose()
        }}
      />

      {createPortal(
        <AnimatePresence>
          {showDropdown && (
            <StyledBusinessDropdown
              ref={refs.setFloating}
              style={{
                top: y || 0,
                left: x || 0,
                position: strategy
              }}
            >
              <Header>
                {t('contacts.form.businessAutoFill.copyBusiness')}
                <HeaderIcon />
              </Header>

              <ScrollableContent>
                <AnimatedContentLoader
                  isLoading={isSearchLongEnough && isLoading}
                  isEmpty={!isSearchLongEnough || searchResults.length === 0}
                  isError={isError}
                  errorDescription={errorMessage}
                  isEmptyDescription={
                    !isSearchLongEnough
                      ? t('contacts.form.businessAutoFill.searchPrompt')
                      : t('contacts.form.businessAutoFill.isEmpty')
                  }
                >
                  {searchResults.map((hit, i) => (
                    <Item
                      key={hit.key}
                      ref={el => {
                        itemRefs.current.set(i, el)
                      }}
                      className={focusIndex === i ? 'keyboard-focused' : undefined}
                      onTouchStart={() => choose(hit)}
                      onMouseDown={() => choose(hit)}
                    >
                      <ItemColumn className="description">{hit.name}</ItemColumn>
                      <ItemColumn>{hit.secondary}</ItemColumn>
                    </Item>
                  ))}
                </AnimatedContentLoader>
              </ScrollableContent>
            </StyledBusinessDropdown>
          )}
        </AnimatePresence>,
        rootRef.current
      )}
    </DropdownWrapper>
  )
}

const LabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;

  svg {
    width: ${({ theme }) => theme.fontSize.sm}rem;
    height: ${({ theme }) => theme.fontSize.sm}rem;
    color: ${({ theme }) => theme.colors.metalGray};
  }
`

export default BusinessSearchDropdown
